import { useCallback, KeyboardEvent, useMemo } from 'react';
import {
  TreeNavKeys,
  NavigableTreeNode,
  onLoadChildren as onLoadChildrenCallback,
} from '../types';
import { transformNodeToNavigable } from '../utils/treeUtils';
import {
  findFirstNode,
  findLastNode,
  findNextNode,
  findParentNode,
  findPreviousNode,
} from '../utils/keyboardNavigationUtils';

interface HandlersProps {
  treeNodes: NavigableTreeNode[];
  updateTreeNode: (nodeId: number, updates: Partial<NavigableTreeNode>) => void;
  onSelect: (nodeId: number) => void;
  onLoadChildren?: onLoadChildrenCallback;
}

export function useTreeNavigationHandlers({
  treeNodes,
  updateTreeNode,
  onSelect,
  onLoadChildren,
}: Readonly<HandlersProps>) {
  /**
   * Move focus to node element.
   */
  const focusNode = useCallback((node: NavigableTreeNode) => {
    const targetElement = document.querySelector(
      `[data-node-id="${node.nodeId}"]`,
    );
    if (targetElement instanceof HTMLElement) {
      targetElement.focus();
    }
  }, []);

  /**
   * Expand or collapse node's children.
   */
  const handleNodeExpansion = useCallback(
    (
      node: NavigableTreeNode,
      shouldExpand: boolean,
    ): NavigableTreeNode | null => {
      updateTreeNode(node.nodeId, { isExpanded: shouldExpand });
      return shouldExpand && node.children?.length ? node.children[0] : null;
    },
    [updateTreeNode],
  );

  /**
   * When Right arrow key is pressed, it's child nodes should be expanded.
   * If the child nodes are already expanded, focus should move to the first child.
   * */
  const handleArrowRight = useCallback(
    (node: NavigableTreeNode): NavigableTreeNode | null => {
      if (!node.hasChildren) {
        return null;
      }
      return handleNodeExpansion(node, true);
    },
    [handleNodeExpansion],
  );

  /**
   * When Left arrow key is pressed and node has expanded children, the children
   * should collapse. When children are already collapsed, focus should move to
   * the parent node.
   */
  const handleArrowLeft = useCallback(
    (node: NavigableTreeNode): NavigableTreeNode | null => {
      if (node.hasChildren && node.isExpanded) {
        return handleNodeExpansion(node, false);
      }
      return findParentNode(treeNodes, node.nodeId);
    },
    [treeNodes, handleNodeExpansion],
  );

  const keyHandlers = useMemo(
    () => ({
      // Selection handlers
      [TreeNavKeys.Enter]: (node: NavigableTreeNode) => {
        onSelect(node.nodeId);
        return null;
      },
      [TreeNavKeys.Space]: (node: NavigableTreeNode) => {
        onSelect(node.nodeId);
        return null;
      },

      // Navigation handlers
      [TreeNavKeys.ArrowRight]: handleArrowRight,
      [TreeNavKeys.ArrowLeft]: handleArrowLeft,
      [TreeNavKeys.ArrowDown]: (node: NavigableTreeNode) =>
        findNextNode(treeNodes, node.nodeId),
      [TreeNavKeys.ArrowUp]: (node: NavigableTreeNode) =>
        findPreviousNode(treeNodes, node.nodeId),
      [TreeNavKeys.Home]: () => findFirstNode(treeNodes),
      [TreeNavKeys.End]: () => findLastNode(treeNodes),
    }),
    [handleArrowLeft, handleArrowRight, onSelect, treeNodes],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent, node: NavigableTreeNode) => {
      const handler = keyHandlers[e.key as keyof typeof keyHandlers];
      if (!handler) {
        return;
      }

      e.preventDefault();
      const targetNode = handler(node);
      if (targetNode) {
        focusNode(targetNode);
      }
    },
    [keyHandlers, focusNode],
  );

  /**
   * Handle expand/collapse of a node.
   */
  const handleItemToggle = useCallback(
    async (node: NavigableTreeNode) => {
      // When selected node has no children, just call the onSelect callback.
      if (!node.hasChildren) {
        onSelect(node.nodeId);
        return;
      }

      /** When node has collapsed children (node.hasChildren & !node.isExpanded),
       * which have not yet been loaded (!node.children), and an `onLoadChildren()`
       * callback function has been given, load the children and add the loaded
       * children to the node tree.
       */
      const shouldLoadChildren =
        !node.isExpanded && !node.children && onLoadChildren;

      /* If node's children should not be loaded, just expand / collapse the node. */
      if (!shouldLoadChildren) {
        updateTreeNode(node.nodeId, { isExpanded: !node.isExpanded });
        return;
      }

      try {
        // Start loading state
        updateTreeNode(node.nodeId, { isLoading: true });

        // Load children
        const children = await onLoadChildren(node.nodeId);

        // Add children to the node tree and end loading state
        updateTreeNode(node.nodeId, {
          children: children.map((child, index) =>
            transformNodeToNavigable(
              child,
              undefined,
              node.level + 1,
              index + 1,
              children.length,
            ),
          ),
          isExpanded: true,
          isLoading: false,
        });
      } catch {
        // If an error occus, end loading state
        updateTreeNode(node.nodeId, { isLoading: false });
      }
    },
    [onSelect, updateTreeNode, onLoadChildren],
  );

  return {
    handleKeyDown,
    handleItemToggle,
  };
}
